import React, { useMemo, useRef, useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";
import { BudgetCategory, BudgetItem, BudgetItemSource, BudgetTotal } from "./types";
import { AgGridReact } from "ag-grid-react";
import {
  CellClassParams,
  CellContextMenuEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  ICellRendererParams,
  ValueSetterParams,
} from "ag-grid-community";
import { useSearchParams } from "react-router-dom";
import classNames from "classnames";
import UnitEditor from "./cellRenderers/UnitEditor";
import {
  getBudgetItemEstimate,
  getBudgetItemVariance,
  getBudgetItemsActual,
  getBudgetItemsEstimate,
  getBudgetItemActual,
  getBudgetItemsVariance,
  toNullableBudgetNumber,
} from "./BudgetItemUtils";
import { FringesCell, FringesCellParams, FringeListState } from "./cellRenderers/FringesCell";
import { fringeLineBlockText, getFringeLineBlock } from "./fringes.utils";
import { useBudgetFringes, useUpdateBudgetItemFringes } from "hooks/useBudget";
import { DetailedBudgetBreadcrumbs } from "./DetailedBudgetBreadcrumbs";
import {
  baseHeaderClasses,
  budgetGridLinkClass,
  defaultColDef,
  RIVET_BUDGET_GRID_CLASS,
  RIVET_BUDGET_GRID_HEIGHT_CLASS,
  RIVET_BUDGET_GRID_WRAPPER_CLASS,
} from "./BudgetGridCommon";
import {
  budgetActualAmountClass,
  budgetEstimateAmountClass,
  budgetMonetaryCellClass,
  budgetVarianceAmountClass,
} from "./budgetGridFormatting";
import { CellRendererBase } from "./CellRendererBase";
import { budgetNonEditableReasons } from "config";
import { formatNumberWithCommas } from "utils";
import { FaTrash } from "react-icons/fa6";
import ConfirmationModal from "components/common/modals/confirmation/ConfirmationModal";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { Plus } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";
import {
  BudgetLineSaver,
  CreateBudgetItemParams,
  DeleteBudgetItemParams,
  DuplicateBudgetItemParams,
  ReorderBudgetItemsParams,
} from "hooks/useBudget/useBudgetMutations";
import { handleToastWithAction, ToastActionButton } from "utils/toasts";
import { handleToast } from "utils";
import {
  getBudgetAccountNumberDuplicateError,
  getBudgetAccountNumberFormatError,
} from "./budgetAccountNumber.validation";
import { toast } from "react-toastify";
import { BudgetRowActions } from "./BudgetRowActions";
import { BudgetLineContextMenu } from "./BudgetLineContextMenu";
import {
  BudgetLineMoveDirection,
  buildReorderPayload,
  canMoveBudgetLineDown,
  canMoveBudgetLineUp,
  moveBudgetLine,
} from "./budgetLineReorder";
import { EDITABLE_TABLE_ROW_HEIGHT } from "components/layout/table/tableStandard";

interface DetailedLevelBudgetGridProps {
  sheetData?: BudgetCategory[];
  budgetItemId: number | null;
  subCategoryId: number | null;
  budgetLocked?: boolean;
  updateBudgetInfo: BudgetLineSaver;
  createBudgetItem?: UseMutationResult<BudgetItem, unknown, CreateBudgetItemParams, unknown>;
  deleteBudgetItemMutation?: UseMutationResult<void, unknown, DeleteBudgetItemParams, unknown>;
  duplicateBudgetItem?: UseMutationResult<BudgetItem, unknown, DuplicateBudgetItemParams, unknown>;
  reorderBudgetItems?: UseMutationResult<void, unknown, ReorderBudgetItemsParams, unknown>;
  budgetId?: string;
}

enum GridColumnIdentifiers {
  RowActions = "row_actions",
  AccountNumber = "account_number",
  Description = "description",
  Fringes = "fringes",
  Unit = "unit",
  Quantity = "quantity",
  Rate = "rate",
  X_Factor = "x_factor",
  Estimate = "estimate",
  Actual = "actual",
  Variance = "variance",
}

const instanceOfBudgetTotal = (object: any): object is BudgetTotal => {
  return "estimate" in object;
};

const instanceOfBudgetItem = (object: unknown): object is BudgetItem => {
  return (
    typeof object === "object" &&
    object !== null &&
    "budget_item_id" in object &&
    typeof (object as BudgetItem).budget_item_id === "number"
  );
};

export const MAX_INPUT_NUMBER_VALUE = 250000000;

const buildGetBudgetRowId =
  (scopeId: number | string) =>
  (params: GetRowIdParams<BudgetItem | BudgetTotal>): string =>
    instanceOfBudgetItem(params.data)
      ? `item-${params.data.budget_item_id}`
      : `budget-total-${scopeId}`;

export const DetailedLevelBudgetGrid: React.FC<DetailedLevelBudgetGridProps> = ({
  budgetItemId,
  subCategoryId,
  sheetData,
  budgetLocked = false,
  updateBudgetInfo,
  createBudgetItem,
  deleteBudgetItemMutation,
  duplicateBudgetItem,
  reorderBudgetItems,
  budgetId,
}) => {
  const { projectId, documentId } = useProjectIdentifiers();

  const { isViewer } = useProjectAuth(projectId);
  const fringeScope = { projectId: projectId ?? "", documentId: documentId ?? "", budgetId };
  const fringesQuery = useBudgetFringes(fringeScope);
  const budgetFringes = fringesQuery.data;
  const updateItemFringes = useUpdateBudgetItemFringes(fringeScope);
  // A failed background refresh keeps the list that loaded. A failure with nothing loaded is
  // unknown, so the menu offers Try again instead of reading as no fringes.
  const fringeList: FringeListState =
    budgetFringes !== undefined
      ? {
          status: "loaded",
          // The list carries each fringe's lines; the line cache only needs the definition.
          fringes: budgetFringes.map(({ items: _items, ...fringe }) => fringe),
        }
      : fringesQuery.isError
        ? {
            status: "failed",
            onRetry: () => void fringesQuery.refetch(),
            isRetrying: fringesQuery.isFetching,
          }
        : { status: "loading" };
  const fringesCellParams: FringesCellParams = {
    fringeList,
    lockedReason: isViewer
      ? budgetNonEditableReasons.viewer
      : budgetLocked
        ? budgetNonEditableReasons.budgetLocked
        : "",
    getBlock: (item) => getFringeLineBlock(item),
    blockReason: (block) =>
      block === "locked" ? budgetNonEditableReasons.lockedAccount : fringeLineBlockText[block],
    onSave: (item, fringes) => updateItemFringes.mutate({ itemId: item.budget_item_id, fringes }),
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [subCategoryGridApi, setSubCategoryGridApi] = useState<GridApi>();
  const [itemGridApi, setItemGridApi] = useState<GridApi>();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<{
    id?: number;
    open: boolean;
  }>({ id: undefined, open: false });

  const [contextLine, setContextLine] = useState<BudgetItem | null>(null);

  // Updated on every render with the current sibling order + parent, so the
  // row-action and context-menu handlers always reorder against fresh data.
  const currentLinesRef = useRef<BudgetItem[]>([]);
  const currentParentIdRef = useRef<number | null>(null);

  // Each setter snapshots the line's values before writing the edit into params.data (the
  // cached line), so a failed save can put the saved values back.
  const saveNewQuantity = (params: ValueSetterParams) => {
    const previous_data = { quantity: params.data.quantity };
    params.data.quantity = params.newValue;
    const newValue = params.newValue === null ? null : Number(params.newValue);
    updateBudgetItem(params.data.budget_item_id, { quantity: newValue }, previous_data);
    return true;
  };

  const saveNewXFactor = (params: ValueSetterParams) => {
    const previous_data = { x_factor: params.data.x_factor };
    params.data.x_factor = params.newValue;
    const newValue = params.newValue === null ? null : Number(params.newValue);
    updateBudgetItem(params.data.budget_item_id, { x_factor: newValue }, previous_data);
    return true;
  };

  const saveNewActual = (params: ValueSetterParams) => {
    const previous_data = { actual_amount: params.data.actual_amount };
    const newValue = toNullableBudgetNumber(params.newValue);
    params.data.actual_amount = newValue;
    updateBudgetItem(params.data.budget_item_id, { actual_amount: newValue }, previous_data);
    return true;
  };

  const saveNewRate = (params: ValueSetterParams) => {
    const previous_data = { rate: params.data.rate, quantity: params.data.quantity };
    const newValue = toNullableBudgetNumber(params.newValue);
    params.data.rate = newValue;
    const update_data: Partial<BudgetItem> = { rate: newValue };

    if (newValue !== null && !params.data.quantity) {
      params.data.quantity = 1;
      update_data.quantity = 1;
    }

    updateBudgetItem(params.data.budget_item_id, update_data, previous_data);
    return true;
  };

  const updateBudgetItem = (
    budgetItemId: number,
    update_data: Partial<BudgetItem>,
    previous_data: Partial<BudgetItem>
  ) => {
    updateBudgetInfo.mutate({ itemId: budgetItemId.toString(), update_data, previous_data });
  };

  const budgetDetailGridRef = useRef<AgGridReact>(null);

  const onBudgetItemClick = (id: any) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(subCategorySearchParamName);
    newSearchParams.set(budgetItemSearchParamName, id);
    setSearchParams(newSearchParams, { replace: true });
  };

  // Editing is purely permission-driven now: anyone who can update the project
  // may edit lines and structure, unless the whole budget is locked.
  const canEditStructure = !isViewer && !budgetLocked;

  const saveNewName = (params: ValueSetterParams) => {
    const newValue = String(params.newValue ?? "").trim();
    if (!newValue) return false;
    const previous_data = { name: params.data.name };
    params.data.name = newValue;
    updateBudgetItem(params.data.budget_item_id, { name: newValue }, previous_data);
    return true;
  };

  const saveNewAccountNumber = (params: ValueSetterParams) => {
    const newValue = String(params.newValue ?? "").trim();
    const itemId = params.data.budget_item_id as number;

    const formatError = getBudgetAccountNumberFormatError(newValue);
    if (formatError) {
      handleToast(formatError, "error");
      return false;
    }

    if (sheetData) {
      const duplicateError = getBudgetAccountNumberDuplicateError(sheetData, newValue, itemId);
      if (duplicateError) {
        handleToast(duplicateError, "error");
        return false;
      }
    }

    const previous_data = { account_number: params.data.account_number };
    params.data.account_number = newValue;
    updateBudgetItem(itemId, { account_number: newValue }, previous_data);
    return true;
  };

  const handleDeleteLine = (item: BudgetItem) => {
    if (!deleteBudgetItemMutation || !canEditStructure) return;

    const snapshot = structuredClone(item);
    const toastId = `budget-delete-${item.budget_item_id}`;

    deleteBudgetItemMutation.mutate(
      { itemId: item.budget_item_id },
      {
        onSuccess: () => {
          handleToastWithAction(
            "Line removed",
            <ToastActionButton
              onClick={() => {
                toast.dismiss(toastId);
                createBudgetItem?.mutate({
                  subCategoryId: item.budget_sub_category_id,
                  item: {
                    name: snapshot.name,
                    account_number: snapshot.account_number,
                    unit: snapshot.unit,
                    quantity: snapshot.quantity,
                    rate: snapshot.rate,
                    x_factor: snapshot.x_factor,
                    parent_item_id: snapshot.parent_item_id,
                  },
                });
              }}
            >
              Undo
            </ToastActionButton>,
            "info",
            "bottom-left",
            toastId,
            8000
          );
        },
      }
    );
  };

  const handleAddLine = () => {
    if (!createBudgetItem || !subCategoryId || !canEditStructure || createBudgetItem.isPending) {
      return;
    }
    const parentItemId = budgetItemId && budgetItemId > 0 ? budgetItemId : null;
    createBudgetItem.mutate({
      subCategoryId,
      item: {
        name: "New line",
        quantity: 1,
        rate: 0,
        ...(parentItemId ? { parent_item_id: parentItemId } : {}),
      },
    });
  };

  const handleDuplicateLine = (itemId: number) => {
    if (!duplicateBudgetItem || !canEditStructure) return;
    duplicateBudgetItem.mutate({ itemId });
  };

  const handleMoveLine = (itemId: number, direction: BudgetLineMoveDirection) => {
    if (!reorderBudgetItems || !canEditStructure) return;
    const reordered = moveBudgetLine(currentLinesRef.current, itemId, direction);
    if (!reordered) return;
    reorderBudgetItems.mutate({
      items: buildReorderPayload(reordered, currentParentIdRef.current),
    });
  };

  const requestDeleteLine = (item: BudgetItem) =>
    item.children?.length > 0
      ? setShowDeleteConfirmation({ id: item.budget_item_id, open: true })
      : handleDeleteLine(item);

  const handleCellContextMenu = (event: CellContextMenuEvent<BudgetItem | BudgetTotal>) => {
    const data = event.data;
    const isEditableLine =
      !!data && instanceOfBudgetItem(data) && isLeafEditable({ data }) && canEditStructure;
    setContextLine(isEditableLine ? (data as BudgetItem) : null);
  };

  const editableCellClass = (params: CellClassParams<any, any>): string =>
    params.data.children?.length > 0 ? "bg-muted/40" : "";

  const isLeafEditable = (params: { data?: BudgetItem | BudgetTotal }) =>
    !!instanceOfBudgetItem(params.data!) &&
    !(params.data.children?.length > 0) &&
    !params.data.locked &&
    !isViewer &&
    !budgetLocked;

  const isStructureEditable = (params: { data?: BudgetItem | BudgetTotal }) =>
    isLeafEditable(params) && canEditStructure;

  const colDefs = useMemo<ColDef<BudgetItem | BudgetTotal>[]>(
    () => [
      {
        headerName: "Acct #",
        field: "account_number",
        colId: GridColumnIdentifiers.AccountNumber,
        width: 100,
        editable: (params) => isStructureEditable(params),
        valueSetter: saveNewAccountNumber,
        cellClass: (params) =>
          classNames({ "border-r-0": instanceOfBudgetTotal(params.data) }, "w-full h-full"),
        cellRenderer: (params: any) => {
          const item = params.data;

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                instanceOfBudgetItem(params.data)
                  ? isViewer
                    ? budgetNonEditableReasons.viewer
                    : !canEditStructure
                      ? budgetNonEditableReasons.budgetLocked
                      : params.data.locked
                        ? budgetNonEditableReasons.lockedAccount
                        : ""
                  : ""
              }
            >
              <div className="flex items-center gap-1">
                {item.source === BudgetItemSource.MANUAL && (
                  <span className="text-[10px] uppercase text-primary/80">M</span>
                )}
                <span>{item.account_number}</span>
              </div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "Description",
        field: "name",
        colId: GridColumnIdentifiers.Description,
        colSpan: (params) => (instanceOfBudgetTotal(params.data) ? 6 : 1),
        cellClass: "w-full h-full",
        flex: 1,
        minWidth: 150,
        editable: (params) => isStructureEditable(params),
        valueSetter: saveNewName,
        cellRenderer: (params: any) => {
          const item = params.data;
          const containsItems = item.children?.length > 0;

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                instanceOfBudgetItem(params.data)
                  ? isViewer
                    ? budgetNonEditableReasons.viewer
                    : !canEditStructure
                      ? budgetNonEditableReasons.budgetLocked
                      : params.data.locked
                        ? budgetNonEditableReasons.lockedAccount
                        : containsItems
                          ? budgetNonEditableReasons.expandedAccount
                          : ""
                  : ""
              }
            >
              <div
                className={classNames(
                  {
                    [budgetGridLinkClass]: containsItems,
                    "font-bold": "estimate" in params.data,
                  },
                  "w-full h-full"
                )}
                {...(containsItems && {
                  onClick: () => params.onBudgetItemClick(item.budget_item_id),
                })}
              >
                <div className="flex gap-3 items-center w-full h-full">
                  <span>{item.name}</span>
                </div>
              </div>
            </CellRendererBase>
          );
        },
        cellRendererParams: { onBudgetItemClick: onBudgetItemClick },
      },
      {
        headerName: "Fringes",
        colId: GridColumnIdentifiers.Fringes,
        cellClass: "w-full h-full",
        width: 140,
        cellRenderer: FringesCell,
        cellRendererParams: fringesCellParams,
      },
      {
        headerName: "Unit",
        field: "unit",
        cellEditor: UnitEditor,
        singleClickEdit: true,
        cellEditorParams: {
          updateBudgetItem: updateBudgetItem,
        },
        colId: GridColumnIdentifiers.Unit,
        width: 100,
        editable: (params) => isLeafEditable(params),
        cellClass: (params) => classNames(editableCellClass(params), "capitalize", "w-full h-full"),
        cellRenderer: (params: any) => {
          const item = params.data;

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                isViewer
                  ? budgetNonEditableReasons.viewer
                  : params.data.children?.length > 0
                    ? budgetNonEditableReasons.expandedAccount
                    : params.data.locked
                      ? budgetNonEditableReasons.lockedAccount
                      : budgetLocked
                        ? budgetNonEditableReasons.budgetLocked
                        : ""
              }
            >
              <div>{item.unit}</div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "Quantity",
        field: "quantity",
        editable: (params) => isLeafEditable(params),
        colId: GridColumnIdentifiers.Quantity,
        width: 100,
        cellClass: (params) =>
          classNames(editableCellClass(params), "ag-right-aligned-cell w-full h-full"),
        cellRenderer: (params: any) => {
          const item = params.data;

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                isViewer
                  ? budgetNonEditableReasons.viewer
                  : params.data.children?.length > 0
                    ? budgetNonEditableReasons.expandedAccount
                    : params.data.locked
                      ? budgetNonEditableReasons.lockedAccount
                      : budgetLocked
                        ? budgetNonEditableReasons.budgetLocked
                        : ""
              }
            >
              <div className="w-full">{item.quantity}</div>
            </CellRendererBase>
          );
        },
        valueSetter: saveNewQuantity,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: {
          min: 0.001,
          max: MAX_INPUT_NUMBER_VALUE,
        },
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
      },
      {
        headerName: "$ Rate",
        field: "rate",
        editable: (params) => isLeafEditable(params),
        colId: GridColumnIdentifiers.Rate,
        cellClass: (params) =>
          classNames(editableCellClass(params), "ag-right-aligned-cell w-full h-full"),
        width: 100,
        cellRenderer: (params: any) => {
          const item = params.data;

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                isViewer
                  ? budgetNonEditableReasons.viewer
                  : params.data.children?.length > 0
                    ? budgetNonEditableReasons.expandedAccount
                    : params.data.locked
                      ? budgetNonEditableReasons.lockedAccount
                      : budgetLocked
                        ? budgetNonEditableReasons.budgetLocked
                        : ""
              }
            >
              <div className="w-full">
                {!(item.children?.length > 0) && (
                  <>{formatNumberWithCommas(Number(item.rate), true)}</>
                )}
              </div>
            </CellRendererBase>
          );
        },
        valueSetter: saveNewRate,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: {
          min: 0,
          max: MAX_INPUT_NUMBER_VALUE,
        },
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
      },
      {
        headerName: "X",
        field: "x_factor",
        editable: (params) => isLeafEditable(params),
        colId: GridColumnIdentifiers.X_Factor,
        width: 80,
        cellClass: (params) =>
          classNames(editableCellClass(params), "ag-right-aligned-cell w-full h-full"),
        cellRenderer: (params: any) => {
          const item = params.data;

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                isViewer
                  ? budgetNonEditableReasons.viewer
                  : params.data.children?.length > 0
                    ? budgetNonEditableReasons.expandedAccount
                    : params.data.locked
                      ? budgetNonEditableReasons.lockedAccount
                      : budgetLocked
                        ? budgetNonEditableReasons.budgetLocked
                        : ""
              }
            >
              <div className="w-full">{item.x_factor}</div>
            </CellRendererBase>
          );
        },
        valueSetter: saveNewXFactor,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: {
          min: 1,
          max: MAX_INPUT_NUMBER_VALUE,
        },
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
      },
      {
        headerName: "$ Estimate",
        colId: GridColumnIdentifiers.Estimate,
        cellClass: classNames(budgetMonetaryCellClass, "ag-right-aligned-cell"),
        width: 120,
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                params.data.children?.length > 0
                  ? budgetNonEditableReasons.expandedAccount
                  : instanceOfBudgetItem(params.data)
                    ? budgetNonEditableReasons.estimate
                    : budgetNonEditableReasons.total
              }
            >
              <div
                className={budgetEstimateAmountClass(
                  classNames("w-full", {
                    "font-bold": instanceOfBudgetTotal(params.data),
                  })
                )}
              >
                {instanceOfBudgetTotal(params.data)
                  ? formatNumberWithCommas(params.data.estimate, true)
                  : formatNumberWithCommas(getBudgetItemEstimate(params.data), true)}
              </div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "$ Actual",
        field: "actual_amount",
        colId: GridColumnIdentifiers.Actual,
        width: 100,
        editable: (params) => isLeafEditable(params),
        cellClass: (params) =>
          classNames(editableCellClass(params), "ag-right-aligned-cell w-full h-full"),
        valueSetter: saveNewActual,
        cellEditor: "agNumberCellEditor",
        cellEditorParams: {
          min: 0,
          max: MAX_INPUT_NUMBER_VALUE,
        },
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
        cellRenderer: (params: any) => {
          const actual = getBudgetItemActual(params.data);

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                isViewer
                  ? budgetNonEditableReasons.viewer
                  : params.data.children?.length > 0
                    ? budgetNonEditableReasons.expandedAccount
                    : params.data.locked
                      ? budgetNonEditableReasons.lockedAccount
                      : instanceOfBudgetTotal(params.data)
                        ? budgetNonEditableReasons.total
                        : budgetLocked
                          ? budgetNonEditableReasons.budgetLocked
                          : ""
              }
            >
              <div
                className={budgetActualAmountClass(
                  classNames({
                    "font-bold": instanceOfBudgetTotal(params.data),
                  })
                )}
              >
                {instanceOfBudgetTotal(params.data)
                  ? formatNumberWithCommas(params.data.actual_amount, true)
                  : actual
                    ? formatNumberWithCommas(actual, true)
                    : ""}
              </div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "$ Variance",
        colId: GridColumnIdentifiers.Variance,
        width: 100,
        cellClass: classNames(budgetMonetaryCellClass, "ag-right-aligned-cell"),
        headerClass: classNames(baseHeaderClasses, "ag-right-aligned-header"),
        cellRenderer: (params: any) => {
          const variance = instanceOfBudgetTotal(params.data)
            ? params.data.variance
            : getBudgetItemVariance(params.data);

          return (
            <CellRendererBase
              params={params}
              nonEditableReason={
                instanceOfBudgetTotal(params.data)
                  ? budgetNonEditableReasons.total
                  : budgetNonEditableReasons.variance
              }
            >
              <div
                className={budgetVarianceAmountClass(
                  variance,
                  classNames({
                    "font-bold": "estimate" in params.data,
                  })
                )}
              >
                {variance ? formatNumberWithCommas(variance, true) : ""}
              </div>
            </CellRendererBase>
          );
        },
      },
      {
        headerName: "",
        colId: GridColumnIdentifiers.RowActions,
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        hide: isViewer,
        pinned: "right",
        suppressHeaderMenuButton: true,
        sortable: false,
        resizable: false,
        cellStyle: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 0,
        },
        cellClass: "budget-row-actions-cell w-full h-full",
        cellRenderer: (params: any) => {
          const item = params.data;
          if (!instanceOfBudgetItem(item) || item.locked || isViewer) return null;

          const containsItems = item.children?.length > 0;
          const lines = currentLinesRef.current;
          const rowActionsDisabled = budgetLocked;
          const rowActionsDisabledTitle = budgetNonEditableReasons.budgetLocked;

          return (
            <div
              className="budget-row-actions flex h-full w-full items-center justify-center"
              onClick={(event) => event.stopPropagation()}
            >
              {containsItems ? (
                <BudgetRowActions
                  disabled={rowActionsDisabled}
                  disabledTitle={rowActionsDisabledTitle}
                  onDelete={() => requestDeleteLine(item)}
                />
              ) : (
                <BudgetRowActions
                  disabled={rowActionsDisabled}
                  disabledTitle={rowActionsDisabledTitle}
                  canMoveUp={canMoveBudgetLineUp(lines, item.budget_item_id)}
                  canMoveDown={canMoveBudgetLineDown(lines, item.budget_item_id)}
                  onMoveUp={() => handleMoveLine(item.budget_item_id, -1)}
                  onMoveDown={() => handleMoveLine(item.budget_item_id, 1)}
                  onDuplicate={() => handleDuplicateLine(item.budget_item_id)}
                  onDelete={() => requestDeleteLine(item)}
                />
              )}
            </div>
          );
        },
      },
    ],
    [
      canEditStructure,
      isViewer,
      budgetLocked,
      sheetData,
      budgetFringes,
      fringesQuery.isError,
      fringesQuery.isFetching,
    ]
  );

  const subCategorySearchParamName = "subCategory";
  const budgetItemSearchParamName = "item";

  const getDetailedBudgetRowData = () => {
    let items: BudgetItem[] = [];
    let path: { name: string; action: () => void }[] = [
      {
        name: "Top sheet",
        action: () => {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete(subCategorySearchParamName);
          newSearchParams.delete(budgetItemSearchParamName);
          setSearchParams(newSearchParams, { replace: true });
        },
      },
    ];

    if (budgetItemId) {
      sheetData?.forEach((category) => {
        category.sub_categories.forEach((subCategory) => {
          subCategory.items.forEach((item) => {
            if (item.budget_item_id == budgetItemId) {
              items = item.children;
              path.push({
                name: subCategory.name,
                action: () => {
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.set(
                    subCategorySearchParamName,
                    subCategory.budget_sub_category_id.toString()
                  );
                  newSearchParams.delete(budgetItemSearchParamName);
                  setSearchParams(newSearchParams, { replace: true });
                },
              });
              path.push({
                name: item.name,
                action: () => {
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.set(budgetItemSearchParamName, item.budget_item_id.toString());
                  newSearchParams.delete(subCategorySearchParamName);
                  setSearchParams(newSearchParams, { replace: true });
                },
              });
            }
          });
        });
      });
    } else if (subCategoryId) {
      sheetData?.forEach((category) => {
        category.sub_categories.forEach((subCategory) => {
          if (subCategory.budget_sub_category_id == subCategoryId) {
            items = subCategory.items;
            path.push({
              name: subCategory.name,
              action: () => {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set(
                  subCategorySearchParamName,
                  subCategory.budget_sub_category_id.toString()
                );
                newSearchParams.delete(budgetItemSearchParamName);
                setSearchParams(newSearchParams, { replace: true });
              },
            });
          }
        });
      });
    }

    // `budgetItemId` is 0 (not null) in the sub-category view, where lines are
    // top-level and must report a `null` parent — the API rejects `0`.
    const parentItemId = budgetItemId && budgetItemId > 0 ? budgetItemId : null;

    let rowData: (BudgetItem | BudgetTotal)[] = [
      ...items,
      {
        name: `${path[path.length - 1].name} Total`,
        estimate: getBudgetItemsEstimate(items),
        actual_amount: getBudgetItemsActual(items),
        variance: getBudgetItemsVariance(items),
      },
    ];

    if (subCategoryId && !subCategoryGridApi) {
      return { rowData: [], path, items, parentItemId };
    }
    if (budgetItemId && !itemGridApi) {
      return { rowData: [], path, items, parentItemId };
    }

    return { rowData, path, items, parentItemId };
  };

  const {
    rowData: detailedBudgetRowData,
    path,
    items: currentLines,
    parentItemId: currentParentId,
  } = getDetailedBudgetRowData();

  currentLinesRef.current = currentLines;
  currentParentIdRef.current = currentParentId;

  const canAddLine = !!subCategoryId && canEditStructure;
  const contextLineId = contextLine?.budget_item_id;
  const contextCanMoveUp =
    contextLineId != null && canMoveBudgetLineUp(currentLines, contextLineId);
  const contextCanMoveDown =
    contextLineId != null && canMoveBudgetLineDown(currentLines, contextLineId);

  const renderBudgetDetailGrid = (
    gridScopeId: number,
    onGridReady: (api: GridApi) => void,
    onGridPreDestroyed?: () => void
  ) => (
    <BudgetLineContextMenu
      activeLine={!isViewer ? contextLine : null}
      canMoveUp={contextCanMoveUp}
      canMoveDown={contextCanMoveDown}
      canAddLine={canAddLine}
      structureDisabled={budgetLocked}
      structureDisabledTitle={budgetNonEditableReasons.budgetLocked}
      onMoveUp={() => contextLine && handleMoveLine(contextLine.budget_item_id, -1)}
      onMoveDown={() => contextLine && handleMoveLine(contextLine.budget_item_id, 1)}
      onDuplicate={() => contextLine && handleDuplicateLine(contextLine.budget_item_id)}
      onDelete={() => contextLine && requestDeleteLine(contextLine)}
      onAddLine={handleAddLine}
    >
      <div
        className={classNames(RIVET_BUDGET_GRID_WRAPPER_CLASS, "mb-4")}
        onContextMenuCapture={() => setContextLine(null)}
      >
        <div
          className={classNames(
            "ag-theme-quartz",
            RIVET_BUDGET_GRID_CLASS,
            RIVET_BUDGET_GRID_HEIGHT_CLASS,
            "budget-detail-grid"
          )}
          role="region"
          aria-label="Budget line items"
          style={{
            width: "100%",
          }}
        >
          <AgGridReact
            rowData={detailedBudgetRowData}
            ref={budgetDetailGridRef}
            columnDefs={colDefs}
            getRowId={buildGetBudgetRowId(gridScopeId)}
            rowClass="bg-transparent text-foreground"
            rowHeight={EDITABLE_TABLE_ROW_HEIGHT}
            defaultColDef={defaultColDef}
            onGridReady={(params) => onGridReady(params.api)}
            onGridPreDestroyed={onGridPreDestroyed}
            onCellContextMenu={handleCellContextMenu}
          />
        </div>
      </div>
    </BudgetLineContextMenu>
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="mb-4 flex items-center justify-between gap-3">
        <DetailedBudgetBreadcrumbs path={path} />
        {subCategoryId && !isViewer ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0">
                <Button
                  variant="ghostBrand"
                  size="sm"
                  onClick={handleAddLine}
                  disabled={budgetLocked}
                  aria-label="Add line"
                >
                  <Plus />
                </Button>
              </span>
            </TooltipTrigger>
            {budgetLocked ? (
              <TooltipContent>{budgetNonEditableReasons.budgetLocked}</TooltipContent>
            ) : null}
          </Tooltip>
        ) : null}
      </div>
      {budgetItemId
        ? renderBudgetDetailGrid(budgetItemId, setItemGridApi, () => setItemGridApi(undefined))
        : subCategoryId
          ? renderBudgetDetailGrid(subCategoryId, setSubCategoryGridApi, () =>
              setSubCategoryGridApi(undefined)
            )
          : null}
      <ConfirmationModal
        title="Are you sure you want to delete this item?"
        isOpen={showDeleteConfirmation.open}
        confirmButtonProps={{
          text: "Delete",
          icon: FaTrash,
          onClick: () => {
            const item = detailedBudgetRowData.find(
              (row) => instanceOfBudgetItem(row) && row.budget_item_id === showDeleteConfirmation.id
            ) as BudgetItem | undefined;
            if (item) handleDeleteLine(item);
            setShowDeleteConfirmation((prevState) => ({ ...prevState, open: false }));
          },
        }}
        cancelButtonProps={{
          text: "Cancel",
          onClick: () => {
            setShowDeleteConfirmation({ id: undefined, open: false });
          },
        }}
      >
        <p className="text-muted-foreground text-sm">
          You are deleting an expanded item. All information will be lost.
        </p>
      </ConfirmationModal>
    </div>
  );
};
